import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

import {
  loadAssumedReturnPct,
  saveAssumedReturnPct,
} from '../core/storage/assumedReturnStorage';
import { authService } from '../modules/Auth/services/authService';
import {
  DEFAULT_ANNUAL_RATE_PCT,
  clampAnnualRatePct,
} from '../utils/investmentCompounding';
import { useAuth } from './AuthContext';
import { useSampleData } from './SampleDataContext';

const SAVE_DEBOUNCE_MS = 400;

type AssumedReturnContextValue = {
  annualRatePct: number;
  hydrated: boolean;
  setAnnualRatePct: (value: number) => void;
  nudgeAnnualRatePct: (delta: number) => void;
  resetToDefault: () => void;
};

const AssumedReturnContext = createContext<AssumedReturnContextValue | null>(null);

/**
 * Shared assumed annual return for Wealth + Grow.
 * Saved on the user profile (like loan rate) so it survives login on any device.
 */
export function AssumedReturnProvider({ children }: PropsWithChildren) {
  const { user, isAuthenticated } = useAuth();
  const { isSampleMode } = useSampleData();
  const [annualRatePct, setAnnualRatePctState] = useState(DEFAULT_ANNUAL_RATE_PCT);
  const [hydrated, setHydrated] = useState(false);
  const dirtyRef = useRef(false);
  const hydratedUserIdRef = useRef<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persistRemote = useCallback(
    (value: number) => {
      if (!isAuthenticated || isSampleMode) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        void (async () => {
          try {
            await authService.updateProfile({ assumedReturnPct: value });
          } catch {
            // Offline / signed out — local cache still holds the value.
          }
        })();
      }, SAVE_DEBOUNCE_MS);
    },
    [isAuthenticated, isSampleMode],
  );

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (isSampleMode) {
      hydratedUserIdRef.current = 'sample';
      dirtyRef.current = false;
      setAnnualRatePctState(DEFAULT_ANNUAL_RATE_PCT);
      setHydrated(true);
      return () => {
        cancelled = true;
      };
    }

    if (!isAuthenticated || !user?.id) {
      hydratedUserIdRef.current = null;
      dirtyRef.current = false;
      setAnnualRatePctState(DEFAULT_ANNUAL_RATE_PCT);
      setHydrated(false);
      return () => {
        cancelled = true;
      };
    }

    const fromMeta = user.meta?.assumedReturnPct;
    const hasMeta = typeof fromMeta === 'number' && Number.isFinite(fromMeta);
    const isNewUser = hydratedUserIdRef.current !== user.id;

    if (isNewUser) {
      hydratedUserIdRef.current = user.id;
      dirtyRef.current = false;

      if (hasMeta) {
        const next = clampAnnualRatePct(fromMeta);
        setAnnualRatePctState(next);
        void saveAssumedReturnPct(next);
        setHydrated(true);
      } else {
        setHydrated(false);
        void (async () => {
          const saved = await loadAssumedReturnPct();
          if (cancelled || hydratedUserIdRef.current !== user.id) return;
          if (saved != null) {
            setAnnualRatePctState(saved);
            persistRemote(saved);
          } else {
            setAnnualRatePctState(DEFAULT_ANNUAL_RATE_PCT);
          }
          setHydrated(true);
        })();
      }
    } else if (hasMeta && !dirtyRef.current) {
      setAnnualRatePctState(clampAnnualRatePct(fromMeta));
      setHydrated(true);
    }

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    isSampleMode,
    persistRemote,
    user?.id,
    user?.meta?.assumedReturnPct,
  ]);

  const setAnnualRatePct = useCallback(
    (value: number) => {
      const next = clampAnnualRatePct(value);
      dirtyRef.current = true;
      setAnnualRatePctState(next);
      if (isSampleMode) return;
      void saveAssumedReturnPct(next);
      persistRemote(next);
    },
    [isSampleMode, persistRemote],
  );

  const nudgeAnnualRatePct = useCallback(
    (delta: number) => {
      setAnnualRatePct(annualRatePct + delta);
    },
    [annualRatePct, setAnnualRatePct],
  );

  const resetToDefault = useCallback(() => {
    dirtyRef.current = true;
    setAnnualRatePctState(DEFAULT_ANNUAL_RATE_PCT);
    if (isSampleMode) return;
    void saveAssumedReturnPct(DEFAULT_ANNUAL_RATE_PCT);
    persistRemote(DEFAULT_ANNUAL_RATE_PCT);
  }, [isSampleMode, persistRemote]);

  const value = useMemo<AssumedReturnContextValue>(
    () => ({
      annualRatePct,
      hydrated,
      setAnnualRatePct,
      nudgeAnnualRatePct,
      resetToDefault,
    }),
    [annualRatePct, hydrated, setAnnualRatePct, nudgeAnnualRatePct, resetToDefault],
  );

  return (
    <AssumedReturnContext.Provider value={value}>{children}</AssumedReturnContext.Provider>
  );
}

export function useAssumedReturn(): AssumedReturnContextValue {
  const ctx = useContext(AssumedReturnContext);
  if (!ctx) {
    throw new Error('useAssumedReturn must be used within AssumedReturnProvider');
  }
  return ctx;
}
